import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Header } from '../Header';
import Footer from '../Footer';
import { InterstitialProvider } from '../InterstitialModal';
import { TermsModal } from '../TermsModal';
import { Seo } from '../Seo';
import { seoByPath } from '../../config/seo';
import './Layout.scss';

const UTILITY_LINKS = [
  { label: 'For Healthcare Providers', href: 'https://www.moretoparkinsonshcp.com/' },
  { label: 'Sign Up for Information', href: '/support#sign-up' },
  { label: 'Learn About Treatment', href: 'https://www.nuplazid.com/', asButton: true },
];

const MAIN_NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Parkinson\'s Hallucinations <br/>and Delusions', href: '/parkinsons-hallucinations-and-delusions' },
  { label: 'Signs & <br/>Symptoms', href: '/signs-and-symptoms' },
  { label: 'Screener', href: '/screener' },
  { label: 'Risks of <br/>Progression', href: '/risks-of-progression' },
  {
    label: 'Navigating <br/>Parkinson\'s <br/>Together',
    href: '/navigating-parkinsons-together',
    children: [
      { label: 'Talking About Symptoms', href: '/talking-about-symptoms' },
      { label: 'For Care Partners', href: '/for-care-partners' },
    ],
  },
  { label: 'Personal <br/>Stories', href: '/personal-stories' },
  {
    label: 'Resources',
    href: '/resources',
    children: [
      { label: 'Support', href: '/support' },
      { label: 'FAQs', href: '/faqs' },
    ],
  },
];

const TERMS_BODY =
  'Acadia Pharmaceuticals Inc. is providing this healthcare provider locator service to assist patients and caregivers find a healthcare provider in their area. No fees have been received by or paid to any healthcare providers for inclusion in this specialist locator directory. Inclusion of a healthcare provider in this directory does not represent an endorsement by or a recommendation from Acadia Pharmaceuticals Inc., nor does it imply that the healthcare provider on the list will determine that Nuplazid is right for you. You are ultimately responsible for the selection of a healthcare professional. This locator tool is just one resource of information available to you.';

const FOOTER_LEGAL_LINKS = [
  { label: 'Privacy Policy', href: 'https://acadia.com/en-us/legal-policies/privacy' },
  { label: 'Terms of Use', href: 'https://acadia.com/en-us/legal-policies/terms' },  // onClick injected in Layout
  { label: 'Cookies Policy', href: 'https://acadia.com/en-us/legal-policies/cookies-policy' },
  { label: 'Your Privacy Choices', href: '#', id: 'display-pref-center', isPrivacyChoices: true, icon: 'privacy' },
  { label: 'Data Privacy Framework', href: 'https://acadia.com/en-us/legal-policies/data-privacy-framework-notice' },
  { label: 'Consumer Health Data Privacy Policy', href: 'https://acadia.com/en-us/legal-policies/privacy#19' },
];

const FOOTER_SOCIAL_LINKS = [
  { label: 'Instagram', href: 'https://www.instagram.com/MoreToParkinsons', icon: 'instagram' },
  { label: 'Facebook', href: 'https://www.facebook.com/people/More-to-Parkinsons/61561211216709/', icon: 'facebook' },
  { label: 'YouTube', href: 'https://www.youtube.com/@MoreToParkinsons', icon: 'youtube' },
  { label: 'Reddit', href: 'https://www.reddit.com/user/MoreToParkinsons/', icon: 'reddit' },
];

export const Layout = ({ element, location }) => {
  // Strip trailing slash so paths match nav hrefs (Gatsby adds trailing slashes)
  const raw = location?.pathname || '/';
  let activeLink = raw;
  if (raw !== '/') {
    while (activeLink.endsWith('/')) {
      activeLink = activeLink.slice(0, -1);
    }
  }

  const [termsOpen, setTermsOpen] = useState(false);

  const openPrivacyChoices = (event) => {
    event?.preventDefault?.();
    if (typeof window === 'undefined') return;

    const wrappedToggle = window.OneTrustWrapper?.ToggleInfoDisplay;
    const oneTrustToggle = window.OneTrust?.ToggleInfoDisplay;

    if (typeof wrappedToggle === 'function') {
      wrappedToggle();
      return;
    }

    if (typeof oneTrustToggle === 'function') {
      oneTrustToggle();
    }
  };

  const legalLinksWithHandlers = FOOTER_LEGAL_LINKS.map((link) =>
    link.isPrivacyChoices
      ? {
          ...link,
          onClick: openPrivacyChoices,
        }
      : link
  );

  return (
    <>
      {/* Google Tag Manager (noscript) */}
      <div dangerouslySetInnerHTML={{__html: '<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-M887LXK" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>'}} />
      <Header utilityLinks={UTILITY_LINKS} mainNavLinks={MAIN_NAV_LINKS} activeLink={activeLink} />
      <main id="main-content">{element}</main>
      <Seo {...(seoByPath[activeLink] || { canonical: activeLink })} />
      <Footer legalLinks={legalLinksWithHandlers} socialLinks={FOOTER_SOCIAL_LINKS} />
      <InterstitialProvider />
      <TermsModal
        open={termsOpen}
        title="Terms and Conditions"
        bodyText={TERMS_BODY}
        closeLabel="Close"
        onClose={() => setTermsOpen(false)}
      />
    </>
  );
};

Layout.propTypes = {
  element: PropTypes.node,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};
